// The Queue › Recently added (Phase 6.3 Part 3, agreed with Kristi Oct 30; mocked up in chat):
// books added lately, books with no shelf yet, and one import's books. No database calls here, so
// the rules can be tested on their own (tests/logic/recent.test.ts).

import { fmtNum } from '../ui/ListFoot';
import { dayText, statusName, todayIn } from '../book/model';
import type { ShelfBook } from '../shelves/model';
import { titleKey } from './model';

/**
 * When (Kristi, Oct 31; was the Showing menu, which mixed when and where from): added in the last
 * N days, or any date. Where a book came from is the Filters panel's Came from row.
 */
export type When = 'recent' | 'any';

export interface RecentView {
  when: When;
  /** No shelf yet: every book with no shelf yet, whatever its date or where it came from (Kristi,
   * Oct 31: don't bury the lede); Came from can still narrow it. */
  noStatus: boolean;
}

/** An import as Came from knows it. */
export interface BatchInfo {
  id: string;
  name: string;
  /** When the import ran. */
  on: string;
}

/** An import's name in the menu, with the day it ran unless the name already holds a date. */
export function batchLabel(b: BatchInfo, today: string): string {
  return /\d{4}-\d{2}-\d{2}|\b(19|20)\d{2}\b|\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/.test(b.name) ? b.name : `${b.name} · ${addedText(b.on, today)}`;
}

/** Every book once, from the shelves' lists (and Reference's, for this tab). */
export function everyBook(lists: ShelfBook[][]): ShelfBook[] {
  const seen = new Map<string, ShelfBook>();
  for (const list of lists) for (const b of list) if (!seen.has(b.id)) seen.set(b.id, b);
  return [...seen.values()];
}

/** Who's in the tab: no shelf yet (any date, anywhere), else by when they were added. */
export function recentRows(books: ShelfBook[], view: RecentView, from: string): ShelfBook[] {
  if (view.noStatus) return books.filter((b) => b.status === null);
  return view.when === 'any' ? books : books.filter((b) => addedDay(b.addedAt) >= from);
}

/** The line under the chips: what the list is showing. */
export function recentLine(view: RecentView, days: number, narrowedTo: string | null, count: number): string {
  const base = view.noStatus
    ? 'Every book with no shelf yet, whatever its date or where it came from'
    : view.when === 'any'
      ? 'Every book on your list, newest added first'
      : `Added in the last ${days} days`;
  return narrowedTo ? `${base} · narrowed to ${narrowedTo}: ${fmtNum(count)} ${count === 1 ? 'book' : 'books'}.` : `${base}.`;
}

/**
 * Came from (Kristi, Oct 31), one value per book: by_hand · discover · claude · other · import:<id>.
 * From Claude shows only for the admin (Kristi); for anyone else Claude's adds count as By hand.
 */
export function cameFromOf(b: Pick<ShelfBook, 'addedHow' | 'id' | 'importBatch'>, byClaude: Set<string>, admin: boolean): string {
  if (b.addedHow === 'import' && b.importBatch) return `import:${b.importBatch}`;
  if (byClaude.has(b.id)) return admin ? 'claude' : 'by_hand';
  if (b.addedHow === 'discover') return 'discover';
  if (b.addedHow === 'other') return 'other';
  return 'by_hand';
}

/**
 * Every import's label (filters review F15, Kristi Nov 2): "<name> · <date>", and imports that
 * would read the same (two typed lists on one day, both "Typed list · Nov 1") get "· 2", "· 3" in
 * the order they ran, so each chip can be told apart.
 */
export function batchLabels(batches: BatchInfo[], today: string): Map<string, string> {
  const out = new Map<string, string>();
  const seen = new Map<string, number>();
  for (const b of [...batches].sort((x, y) => x.on.localeCompare(y.on) || x.id.localeCompare(y.id))) {
    const base = batchLabel(b, today);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    out.set(b.id, n === 1 ? base : `${base} · ${n}`);
  }
  return out;
}

/** Imports older than this many days sit behind "+ N older" in Came from (F15). */
export const CAME_FROM_RECENT_DAYS = 90;

/**
 * Came from's chips, counted on the books given (pass them narrowed by the other rows: F10);
 * empty chips left out unless on. The ways you add by hand first, then imports newest first, those
 * from the last 90 days ahead of older ones; `shown` is how many come before "+ N older".
 */
export function cameFromChips(
  books: Pick<ShelfBook, 'addedHow' | 'id' | 'importBatch'>[],
  batches: BatchInfo[],
  byClaude: Set<string>,
  admin: boolean,
  on: string[],
  today: string,
): { id: string; label: string; count: number }[] & { shown?: number } {
  const n = new Map<string, number>();
  for (const b of books) {
    const v = cameFromOf(b, byClaude, admin);
    n.set(v, (n.get(v) ?? 0) + 1);
  }
  const keep = (c: { id: string; count: number }) => c.count > 0 || on.includes(c.id);
  const labels = batchLabels(batches, today);
  const ways: [string, string][] = [
    ['by_hand', 'By hand'],
    ['discover', 'From Discover'],
    ...(admin ? ([['claude', 'From Claude']] as [string, string][]) : []),
    ['other', 'Other'],
  ];
  const cut = (() => {
    if (!today) return '';
    const d = new Date(`${today}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - CAME_FROM_RECENT_DAYS);
    return d.toISOString().slice(0, 10);
  })();
  const newest = [...batches].sort((x, y) => y.on.localeCompare(x.on) || y.id.localeCompare(x.id));
  const chip = (b: BatchInfo) => ({ id: `import:${b.id}`, label: labels.get(b.id)!, count: n.get(`import:${b.id}`) ?? 0 });
  const head = [...ways.map(([id, label]) => ({ id, label, count: n.get(id) ?? 0 })), ...newest.filter((b) => b.on.slice(0, 10) >= cut).map(chip)].filter(keep);
  const older = newest.filter((b) => b.on.slice(0, 10) < cut).map(chip).filter(keep);
  const out = [...head, ...older] as { id: string; label: string; count: number }[] & { shown?: number };
  if (older.length) out.shown = head.length;
  return out;
}

/** How added (Kristi, Oct 30): From Claude is worked out from Claude's own change record (never shared). */
export function howAdded(b: Pick<ShelfBook, 'addedHow' | 'id'>, byClaude: Set<string>): string {
  if (byClaude.has(b.id)) return 'From Claude';
  switch (b.addedHow) {
    case 'import':
      return 'Import';
    case 'discover':
      return 'From Discover';
    case 'other':
      return 'Other';
    default:
      return 'By hand';
  }
}

export type RecentSortKey = 'added' | 'title' | 'how' | 'status' | 'readtype' | 'interest' | 'goodreads' | 'copy';
export interface RecentSort {
  key: RecentSortKey;
  reversed: boolean;
}

export const RECENT_SORTS: [RecentSortKey, string][] = [
  ['added', 'Newest added'],
  ['interest', 'Interest'],
  ['goodreads', 'Goodreads'],
  ['how', 'How added'],
  ['status', 'Shelf'],
  ['readtype', 'Read type'],
  ['copy', 'Copy'],
  ['title', 'Title'],
];

const TYPE_ORDER = ['light', 'absorbing', 'demanding', 'study'];
const STATUS_ORDER = ['currently_reading', 'up_next', 'future_reads', 'finished', 'not_for_me', 'reference', null];

/**
 * The rows in a sort (newest added first by default). Every sort breaks ties by newest added,
 * then title; empty values go last whichever way it runs.
 */
export function sortRecent<R extends ShelfBook>(rows: R[], sort: RecentSort, extra: { how: (r: R) => string; copy: (r: R) => string | null }): R[] {
  const dir = sort.reversed ? -1 : 1;
  const newest = (a: R, b: R) => b.addedAt.localeCompare(a.addedAt) || titleKey(a.title).localeCompare(titleKey(b.title));
  const val = (r: R): number | string | null => {
    switch (sort.key) {
      case 'title':
        return titleKey(r.title);
      case 'how':
        return extra.how(r).toLocaleLowerCase('en-US');
      case 'status':
        return STATUS_ORDER.indexOf(r.status);
      case 'readtype': {
        const i = r.readType ? TYPE_ORDER.indexOf(r.readType) : -1;
        return i >= 0 ? i : r.readTypeName ? 4 : null;
      }
      case 'interest':
        return r.interest === null ? null : -r.interest;
      case 'goodreads':
        return r.goodreads === null ? null : -r.goodreads;
      case 'copy':
        return extra.copy(r)?.toLocaleLowerCase('en-US') ?? null;
      default:
        return null;
    }
  };
  return [...rows].sort((a, b) => {
    if (sort.key === 'added') return newest(a, b) * dir;
    const x = val(a);
    const y = val(b);
    if (x !== y) {
      if (x === null) return 1;
      if (y === null) return -1;
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      if (c) return c * dir;
    }
    return newest(a, b);
  });
}

/** The Status column's words. */
export const statusText = (b: Pick<ShelfBook, 'status'>) => statusName(b.status);

/** The Pacific day a book was added (R8 W3): a timestamp's own day there, not its UTC date. */
export function addedDay(iso: string): string {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : todayIn('America/Los_Angeles', d);
}

/** The Added column, through the one formatter: "Oct 29" this year, "Jul 1, 2024" before it. */
export function addedText(iso: string, today: string): string {
  return iso ? dayText(addedDay(iso), today) : '';
}
